import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';

import { NoteMapper } from '@shared-classes';
import {
  DianCompany,
  XMLResponse,
  Note,
  CreditNoteRequest,
  DebitNoteRequest,
  DianNote,
  NotePdf,
} from '@shared-models';
import {
  AlertService,
  CalculateNotePdfService,
  ElectronicNotePdfService,
} from '@shared-services';

import { IStorageCompany } from 'src/app/@interfaces/company.interface';
import { QuotationsService } from 'src/app/@pages/quotations/quotations.service';

import { DianInvoiceService } from './dian-invoice.service';
import { DianService } from './dian.service';
import { CookieAuthService } from '../../../../@shared/storage-variables/cookie-auth.service';

@Injectable({
  providedIn: 'root',
})
export class DianNoteService {
  private quotationService = inject(QuotationsService);
  private cookieAuthService = inject(CookieAuthService);
  private alertService = inject(AlertService);
  private spinner = inject(NgxSpinnerService);
  private destroyRef = inject(DestroyRef);
  private translate = inject(TranslateService);
  private dianInvoiceService = inject(DianInvoiceService);
  private dianService = inject(DianService);
  private calculateNotePdfService = inject(CalculateNotePdfService);
  private electronicNotePdfService = inject(ElectronicNotePdfService);

  companyId = '';
  companyName = '';
  companyObject = {} as IStorageCompany;

  getAuthValues() {
    this.companyObject = this.cookieAuthService.getCompanyObject!;
    this.companyId = this.companyObject.Id_company!.toString();
  }

  async sendDian(noteId: number) {
    try {
      this.getAuthValues();
      const note = await this.getNote(noteId);
      if (!note.billyNotes?.length) {
        this.alertService.open({
          title: this.translate.instant('global.notes.internal-note-title'),
          message: this.translate.instant(
            'global.notes.internal-note-description',
          ),
          type: 'success',
          showClose: true,
        });
        return;
      }
      const dianCompany = await this.dianInvoiceService.createDianCompany(
        note.billyNotes[0].voucher!,
        this.companyId.toString(),
      );
      this.spinner.show();
      const dianResponse = await this.sendRequest(note, dianCompany);
      return dianResponse;
    } catch (error) {
      // Hide the spinner on any rejection path (DIAN rejection, company/prefix
      // build failure, network error) so the error dialog stays dismissible.
      // Matches dian-adjustment-note / dian-supporting-document.
      this.spinner.hide();
      return Promise.reject(error);
    }
  }

  async getNote(id: number) {
    return new Promise<Note>((resolve) => {
      this.quotationService
        .getSingleNote(id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((item: Note) => {
          resolve(item);
        });
    });
  }

  async sendRequest(
    note: Note,
    dianCompany: DianCompany,
    withoutReference = false,
  ) {
    return new Promise((resolve, reject) => {
      const dianNote = NoteMapper.mapper(note, withoutReference);
      if (!dianNote.invoice.billyInvoice?.cufe) {
        this.spinner.hide();
        this.showErrorAlert(
          this.translate.instant('global.dian.notes.cufe-error'),
        );
        reject('Cufe code for the related invoice is not available.');
      } else {
        const dianRequiredObject: CreditNoteRequest | DebitNoteRequest =
          this.buildDianRequest(dianCompany, dianNote);
        this.dianService.sendBillSyncNote(dianRequiredObject).subscribe(
          async (dianResponse: XMLResponse) => {
            if (
              dianResponse.StatusCode === '00' ||
              (dianResponse.string && dianResponse.string.includes('Regla: 90'))
              // Rule: 90 is when the document has already been processed previously, then proceed to save the cufe.
            ) {
              await this.onSuccessFullRequest(note, dianResponse);
              resolve(dianResponse);
            } else if (
              !withoutReference &&
              this.isAcceptedInvoiceRejection(dianNote, dianResponse) &&
              this.annulsTheInvoice(dianNote)
            ) {
              // An accepted invoice cannot be annulled at all: a referenced note
              // is blocked by LGC15 and an unreferenced one by CBF03a, so there
              // is nothing to offer — only an explanation.
              this.alertService.open({
                title: this.translate.instant(
                  'global.dian.notes.annulment-not-allowed-title',
                ),
                message: this.translate.instant(
                  'global.dian.notes.annulment-not-allowed-description',
                ),
                type: 'error',
                showClose: true,
              });
              reject(dianResponse.StatusMessage);
            } else if (
              !withoutReference &&
              this.isAcceptedInvoiceRejection(dianNote, dianResponse)
            ) {
              // The invoice is already accepted, so DIAN refuses any note that
              // references it. The same note can still be sent with no
              // BillingReference, keeping its internal link to the invoice.
              this.confirmSendWithoutReference().subscribe({
                accept: () => {
                  this.spinner.show();
                  this.sendRequest(note, dianCompany, true).then(
                    resolve,
                    reject,
                  );
                },
                reject: () => {
                  this.alertService.open(
                    this.dianInvoiceService.buildAlert(dianResponse),
                  );
                  reject(dianResponse.StatusMessage);
                },
                close: () => {
                  this.alertService.open(
                    this.dianInvoiceService.buildAlert(dianResponse),
                  );
                  reject(dianResponse.StatusMessage);
                },
              });
            } else {
              this.alertService.open(
                this.dianInvoiceService.buildAlert(dianResponse),
              );
              reject(dianResponse.StatusMessage);
            }
          },
          async (error) => {
            this.spinner.hide();
            this.showErrorAlert(error);
            reject(error);
          },
        );
      }
    });
  }

  /**
   * DIAN rules LGC15 (credit note) and LGC16 (debit note): the referenced
   * invoice was already accepted by the customer, so no referenced note is
   * allowed on it.
   */
  private isAcceptedInvoiceRejection(
    dianNote: DianNote,
    dianResponse: XMLResponse,
  ): boolean {
    const rule = dianNote.tipo_nota === 'Credit' ? 'LGC15' : 'LGC16';
    const responseText = `${dianResponse.string ?? ''} ${
      dianResponse.StatusMessage ?? ''
    }`.toUpperCase();
    return responseText.includes(rule);
  }

  /**
   * DIAN rules CBF03a and CAD02a: concept 2, "Anulación de factura electrónica"
   * (table 13.2.4), demands CustomizationID 20, so a credit note that annuls an
   * invoice can never be sent without a billing reference. Debit notes map
   * concept 2 to "Gastos por cobrar" (table 13.2.5) and carry no such rule.
   */
  private annulsTheInvoice(dianNote: DianNote): boolean {
    return dianNote.tipo_nota === 'Credit' && String(dianNote.concepto) === '2';
  }

  private confirmSendWithoutReference() {
    this.spinner.hide();
    return this.alertService.open({
      title: this.translate.instant('global.dian.notes.accepted-invoice-title'),
      message: this.translate.instant(
        'global.dian.notes.accepted-invoice-description',
      ),
      type: 'warning',
      acceptText: this.translate.instant(
        'global.dian.notes.accepted-invoice-accept',
      ),
      rejectText: this.translate.instant(
        'global.dian.notes.accepted-invoice-reject',
      ),
    });
  }

  private buildDianRequest(
    dianCompany: DianCompany,
    dianNote: DianNote,
  ): CreditNoteRequest | DebitNoteRequest {
    if (dianNote.tipo_nota === 'Credit') {
      return {
        company: dianCompany,
        credit_note: dianNote,
      } as CreditNoteRequest;
    } else if (dianNote.tipo_nota === 'Debit') {
      return {
        company: dianCompany,
        debit_note: dianNote,
      } as DebitNoteRequest;
    } else {
      return {} as CreditNoteRequest | DebitNoteRequest;
    }
  }

  async onSuccessFullRequest(note: Note, dianResponse: XMLResponse) {
    return new Promise<unknown>((resolve, reject) => {
      (async () => {
        try {
          const clientData = this.dianInvoiceService.prepareClientData(
            note.invoice,
            note.billyNotes[0].note_number,
            note.tipo_nota === 'Credito' ? '91' : '92',
          );
          const pdfFile = await this.generateInvoicePdf(note, dianResponse);
          const xmlFile = dianResponse.AttachedDocument || '';
          this.quotationService
            .setCudeNote(
              parseInt(note.id_nota.toString()),
              dianResponse.XmlDocumentKey,
              dianResponse.StatusCode,
              clientData,
              pdfFile,
              xmlFile,
            )
            .subscribe({
              next: (result) => {
                this.spinner.hide();
                this.alertService.open(
                  this.dianInvoiceService.buildAlert(dianResponse),
                );
                resolve(result);
              },
              error: (error) => {
                this.spinner.hide();
                this.showErrorAlert(error);
                reject(error);
              },
            });
        } catch (error) {
          this.spinner.hide();
          this.alertService.open({
            title: 'Error',
            message: 'Error preparing invoice data for email',
            type: 'error',
            showClose: true,
          });
          reject(error);
        }
      })();
    });
  }

  private async generateInvoicePdf(
    note: Note,
    dianResponse: XMLResponse,
  ): Promise<string> {
    try {
      // Configure the calculation service with the note data
      await this.calculateNotePdfService.setNote(
        parseInt(note.id_nota.toString()),
        this.companyId,
        false,
      );

      // Update QR URL with DIAN and CUDE information
      this.calculateNotePdfService.qrUrl =
        this.dianInvoiceService.generateQrUrl(dianResponse);

      const pdfObject: NotePdf = {
        open: false,
        noteObject: this.calculateNotePdfService.noteObject,
        noteCount: this.calculateNotePdfService.noteCount,
        noteValues: this.calculateNotePdfService.noteValues,
        subtotal: this.calculateNotePdfService.subtotal,
        taxCalculated: this.calculateNotePdfService.taxCalculated,
        otherTaxCalculated: this.calculateNotePdfService.otherTaxes,
        discount: this.calculateNotePdfService.discount,
        total: this.calculateNotePdfService.total,
        qrUrl: this.calculateNotePdfService.qrUrl,
        company: this.companyId,
        cude: dianResponse.XmlDocumentKey,
      };
      // Generate the PDF using the DIAN electronic note service.
      const pdfDocument =
        await this.electronicNotePdfService.createNotePdf(pdfObject);

      // Convert PDF to base64
      return new Promise((resolve) => {
        if (pdfDocument && pdfDocument.getBase64) {
          pdfDocument.getBase64((base64: string) => {
            resolve(base64);
          });
        } else {
          resolve('');
        }
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      return '';
    }
  }

  private showErrorAlert(error: undefined) {
    this.alertService.open({
      title: this.translate.instant('global.dian.notes.error'),
      message: error!,
      type: 'error',
      showClose: true,
    });
  }
}
